import React, { useEffect, useState, useRef } from 'react';
import { Phone, Menu, X, Globe } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from './LanguageContext';
export function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);
  const {
    lang,
    setLang,
    t
  } = useLanguage();
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(event.target as Node)) {
        setIsLangOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const handleLangSelect = (newLang: 'es' | 'en') => {
    setLang(newLang);
    setIsLangOpen(false);
  };
  return <>
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-barber-black/95 backdrop-blur-md border-b border-barber-lightGray py-3' : 'bg-transparent py-5'}`} data-id="element-286">
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex justify-between items-center" data-id="element-287">
          <div className="flex items-center gap-2" data-id="element-288">
            <span className="font-logo text-3xl text-barber-text" data-id="element-289">
              VillaCon<span className="text-barber-gold" data-id="element-290">BarberShop</span>
            </span>
          </div>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-6" data-id="element-291">
            <div className="flex items-center gap-2 text-barber-gold font-bold" data-id="element-292">
              <span className="text-sm tracking-widest uppercase" data-id="element-293">
                {t('navbar.openUntil')}
              </span>
            </div>

            {/* Language Toggle */}
            <div className="relative" ref={langRef} data-id="element-294">
              <button onClick={() => setIsLangOpen(!isLangOpen)} className="flex items-center gap-2 text-barber-text hover:text-barber-gold transition-colors p-2" data-id="element-295">
                <Globe size={20} data-id="element-296" />
                <span className="text-sm font-bold uppercase" data-id="element-297">{lang}</span>
              </button>

              <AnimatePresence data-id="element-298">
                {isLangOpen && <motion.div initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: 10
              }} className="absolute top-full right-0 mt-2 w-36 bg-barber-gray border border-barber-lightGray rounded-sm shadow-xl overflow-hidden" data-id="element-299">
                    <button onClick={() => handleLangSelect('es')} className={`w-full text-left px-4 py-3 text-sm hover:bg-barber-lightGray transition-colors ${lang === 'es' ? 'text-barber-gold font-bold' : 'text-barber-text'}`} data-id="element-300">
                      🇪🇸 Español
                    </button>
                    <button onClick={() => handleLangSelect('en')} className={`w-full text-left px-4 py-3 text-sm hover:bg-barber-lightGray transition-colors ${lang === 'en' ? 'text-barber-gold font-bold' : 'text-barber-text'}`} data-id="element-301">
                      🇺🇸 English
                    </button>
                  </motion.div>}
              </AnimatePresence>
            </div>

            <a href="tel:3476058521" className="bg-barber-red hover:bg-barber-redHover text-white font-heading font-bold tracking-wider px-6 py-2.5 rounded-sm transition-colors flex items-center gap-2" data-id="element-302">
              <Phone size={18} data-id="element-303" />
              {t('navbar.callUs')}
            </a>
          </div>

          {/* Mobile Toggle */}
          <div className="md:hidden flex items-center gap-4" data-id="element-304">
            <button onClick={() => {
            setLang(lang === 'es' ? 'en' : 'es');
          }} className="flex items-center gap-1 text-barber-text hover:text-barber-gold transition-colors" data-id="element-305">
              <Globe size={20} data-id="element-306" />
              <span className="text-xs font-bold uppercase" data-id="element-307">{lang}</span>
            </button>
            <button className="text-barber-text p-2" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} data-id="element-308">
              {isMobileMenuOpen ? <X size={24} data-id="element-309" /> : <Menu size={24} data-id="element-310" />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      <AnimatePresence data-id="element-311">
        {isMobileMenuOpen && <motion.div initial={{
        opacity: 0,
        y: -20
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -20
      }} className="fixed inset-0 z-40 bg-barber-black pt-24 px-4 pb-8 flex flex-col justify-center items-center md:hidden" data-id="element-312">
            <div className="flex flex-col items-center gap-8 w-full max-w-sm" data-id="element-313">
              <p className="font-heading text-xl text-barber-gold tracking-widest uppercase text-center" data-id="element-314">
                {t('navbar.openUntil')}
              </p>
              <p className="text-barber-muted text-center" data-id="element-315">
                926A East Tremont Ave
                <br data-id="element-316" />
                Bronx, NY 10460
              </p>
              <a href="tel:3476058521" className="w-full bg-barber-red hover:bg-barber-redHover text-white font-heading font-bold text-xl tracking-wider py-4 rounded-sm transition-colors flex justify-center items-center gap-2" onClick={() => setIsMobileMenuOpen(false)} data-id="element-317">
                <Phone size={24} data-id="element-318" />
                (347) 605-8521
              </a>
            </div>
          </motion.div>}
      </AnimatePresence>
    </>;
}